import {
  useCallback,
  useState,
  ChangeEvent,
  useContext,
  useEffect,
} from "react";
import { LoginContext } from "../context";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import {
  performLogin,
  performLoginForQrCode,
  performLoginForResendNotify,
  setScreen,
} from "../../actions/loginActions";
import { LoginFormType } from "../schemas/login";
import { useTranslation } from "react-i18next";
import { useErrorContext } from "../../_shared/context/ErrorContext";
import { LoginScreen } from "../types";
import { LOGIN_CHANNEL } from "@delorean/_shared/constants";
import { PasswordResetScreen } from "@delorean/forgetPassword/types";
import { useNavigate } from "react-router-dom";
import * as loginRoutes from "../../_shared/routing";
import {
  closeToken,
  setUserAuthenticated,
  setUserId,
} from "@delorean/utils/seapUtils";
import {
  CaptchaActionEnum,
  useCaptcha,
} from "@delorean/_shared/hooks/useCaptcha";
import { performPasswordResendNotify, performResetForgetPasswordForQRCode } from "@delorean/actions/resetPasswordActions";

export const useLoginForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getCaptchaToken } = useCaptcha();
  const [isCaptchaActive, setIsCaptchaActive] = useState(false);
  const {
    password,
    setPassword,
    contractCode,
    setContractCode,
    setUserCode,
    userCode,
  } = useContext(LoginContext);

  const navigate = useNavigate();

  const {
    error,
    loading,
    screen,
    userCode: userCodeFromStore,
    contractCode: contractCodeFromState,
  } = useSelector((state: RootStateOrAny) => state.login);
  const isAuthenticated = useSelector(
    (state: RootStateOrAny) => state.login.isAuthenticated,
  );
  // STATE
  const challenge = useSelector(
    (state: RootStateOrAny) => state.login.mfaData.channel,
  );
  const xOldTransaction = useSelector(
    (state: RootStateOrAny) => state.login.mfaData.transactionId,
  );

  const { showError } = useErrorContext();
  const [confirmation, setConfirmation] = useState<boolean>(false);

  const setCurrentScreen = useCallback(
    (screenType: LoginScreen | PasswordResetScreen) => {
      dispatch(setScreen(screenType));
    },
    [dispatch],
  );

  const onContractCodeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setContractCode(e.target.value);
    },
    [setContractCode],
  );
  const onUserCodeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setUserCode(e.target.value);
    },
    [setUserCode],
  );
  const onPasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
    },
    [setPassword],
  );


  const submit = async (values: LoginFormType) => {
    try {
      // Delay intenzionale prima di iniziare le operazioni SEAP
      await new Promise((resolve) => setTimeout(resolve, 500));
      setUserId(values.userCode || "");
      const moreTxi = await closeToken();

      let captchaToken: string | undefined;

      if (isCaptchaActive) {
        try {
          captchaToken = await getCaptchaToken(CaptchaActionEnum.LOGIN);
        } catch (error) {
          console.error("Error obtaining captcha token:", error);
          if (error && typeof error === "object" && "message" in error) {
            throw new Error((error as { message: string }).message);
          } else {
            throw new Error("An unknown login captcha error occurred");
          }
        }
      }

      dispatch(
        performLogin(
          values.contractCode,
          values.userCode,
          values.password,
          moreTxi,
          captchaToken,
        ),
      );
      setUserAuthenticated(true);
    } catch (error) {
      throw new Error("login form error");
    }
  };

  const submitLoginForQrCode = async () => {
    try {
      // Delay intenzionale prima di iniziare le operazioni SEAP
      await new Promise((resolve) => setTimeout(resolve, 500));
      setUserId(userCodeFromStore || "");
      const moreTxi = await closeToken();
      dispatch(performLoginForQrCode(moreTxi, xOldTransaction));
      setUserAuthenticated(true);
    } catch (error) {
      throw new Error("login form error for Qr code");
    }
  };

  const submitResetPasswordForQrCode = async () => {
    try {
      // Delay intenzionale prima di iniziare le operazioni SEAP
      await new Promise((resolve) => setTimeout(resolve, 500));
      setUserId(userCodeFromStore || "");
      const moreTxi = await closeToken();
      dispatch(performResetForgetPasswordForQRCode(moreTxi, xOldTransaction));
      setUserAuthenticated(true);
    } catch (error) {
      throw new Error("login form error for Qr code");
    }
  };

  // this function is used to handle the click on "Resend notification" button in the app notification screen,
  // it will call the same endpoint of the first notification with the same transactionId to trigger
  // a new notification on the user device
  const submitNewNotify = async () => {
    try {
      // Delay intenzionale prima di iniziare le operazioni SEAP
      await new Promise((resolve) => setTimeout(resolve, 500));
      setUserId(userCodeFromStore || "");
      const moreTxi = await closeToken();
      console.log("submitNewNotify called");
      dispatch(performLoginForResendNotify(xOldTransaction, moreTxi));
      setUserAuthenticated(true);
    } catch (error) {
      throw new Error("login form error");
    }
  };

  // this function do the same operation for both change password flow reset and forget
  const submitNewNotifyFromChangePassword = async () => {
    try {
      // Delay intenzionale prima di iniziare le operazioni SEAP
      await new Promise((resolve) => setTimeout(resolve, 500));
      setUserId(userCodeFromStore || "");
      const moreTxi = await closeToken();
      dispatch(performPasswordResendNotify(xOldTransaction, moreTxi));
      setUserAuthenticated(true);
    } catch (error) {
      throw new Error("login form error");
    }
  };

  const returnToLogin = useCallback(() => {
    setConfirmation(false);
    // resetLogin();
  }, [setConfirmation]);

  const goToResetPassword = () => {
    setCurrentScreen(PasswordResetScreen.CREDENTIAL_RESETPASSWORD);
    navigate(loginRoutes.resetPassword);
  };

  useEffect(() => {
    if (error) {
      switch (error) {
        default:
          showError(t("errors.messageOne"));
      }
      setIsCaptchaActive(true);
    } else {
      setIsCaptchaActive(false);
    }
  }, [error]);

  /*
   * handle app/token flow after first login step
   * handle reset temporary psw case with redirect to
   */
  useEffect(() => {
    // Check if we're in the reset password flow
    const isInResetPasswordFlow = location.pathname.includes("resetPassword");

    // Skip login-specific logic if we're in reset password flow
    if (isInResetPasswordFlow) {
      // Only handle the redirect for temporary password reset
      if (screen === PasswordResetScreen.RESET_PASSWORD_TEMP) {
        navigate("/resetPassword");
      }
      return; // Exit early to avoid interfering with reset password flow
    }

    // Original login flow logic - only runs when NOT in reset password
    if (!confirmation && screen === LoginScreen.APP_NOTIFICATION) {
      setCurrentScreen(LoginScreen.FAILURE);
    }
    if (isAuthenticated && screen !== LoginScreen.APP_QRCODE) {
      setConfirmation(true);
      switch (challenge) {
        case LOGIN_CHANNEL.TOKEN:
          setCurrentScreen(LoginScreen.PHYS_TOKEN);
          break;
        case LOGIN_CHANNEL.APP_DEVICE:
          setCurrentScreen(LoginScreen.APP_NOTIFICATION);
          break;
        default:
          setCurrentScreen(LoginScreen.DEFAULT);
      }
      /* cancel login procedure: come from Token or QRcode verification step */
    } else if (
      !isAuthenticated &&
      (screen === LoginScreen.APP_QRCODE ||
        screen === LoginScreen.PHYS_TOKEN ||
        screen === LoginScreen.APP_NOTIFICATION)
    ) {
      setConfirmation(false);
      setCurrentScreen(LoginScreen.DEFAULT);
    }

    /* first login access case */
    if (screen == PasswordResetScreen.RESET_PASSWORD_TEMP) {
      navigate("/resetPassword");
    }
  }, [isAuthenticated, screen]);

  return {
    contractCode,
    onContractCodeChange,
    userCode,
    onUserCodeChange,
    password,
    onPasswordChange,
    loading,
    submit,
    submitLoginForQrCode,
    confirmation,
    setConfirmation,
    returnToLogin,
    screen,
    setScreen: setCurrentScreen,
    goToResetPassword,
    submitNewNotify,
    submitNewNotifyFromChangePassword,
    submitResetPasswordForQrCode,
    error,
  };
};
